import { confusablesFor, type Skill } from '../../types';
import { getSpecies, taxonomicPosition } from '../region';

/** Seedable PRNG (mulberry32) so daily challenges are deterministic. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(arr: T[], rnd: () => number): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * How many taxonomic neighbours the broad tiers draw from.
 *
 * Strictly nearest would hand the same three birds back every time, which is
 * its own kind of free mark — a learner who sees Turkey Vulture always paired
 * with the same two hawks learns the pairing, not the bird. Twelve is four
 * times the slots a question needs, so there's real variety inside a
 * neighbourhood that stays recognisably related.
 */
const NEIGHBOURHOOD = 12;

/**
 * Songbird or not. The one place a linear taxonomic ordering badly misleads:
 * Falconiformes sits immediately before Passeriformes, so an American Kestrel's
 * nearest neighbours are vireos and flycatchers — birds a learner discards
 * without a thought. It's also the coarsest division a birder actually uses, so
 * respecting it costs nothing elsewhere.
 */
function isSongbird(code: string): boolean {
  return getSpecies(code)?.order === 'Passeriformes';
}

/**
 * The `window` species from `codes` taxonomically closest to `targetCode`.
 *
 * Distance is over manifest position (see `taxonomicPosition`), and ties — a
 * bird one place above and one place below — are broken by position so the
 * window itself is deterministic; `take` shuffles it afterwards. Falls through
 * to the whole list when positions are unavailable, which is what happens in
 * tests that run against a bare code pool with no region installed.
 */
function nearestTo(targetCode: string, codes: string[], window: number): string[] {
  const home = taxonomicPosition(targetCode);
  if (home === undefined) return codes;
  const placed = codes
    .map((code) => ({ code, pos: taxonomicPosition(code) }))
    .filter((x): x is { code: string; pos: number } => x.pos !== undefined);
  if (!placed.length) return codes;
  return placed
    .sort((a, b) => Math.abs(a.pos - home) - Math.abs(b.pos - home) || a.pos - b.pos)
    .slice(0, window)
    .map((x) => x.code);
}

/**
 * Build `count` distractor species codes for `targetCode`, drawn from `skill`'s
 * confusable cluster and then down a graded fallback. Never includes the
 * target. `rnd` lets callers make the choice deterministic (daily challenge).
 *
 * The cascade — cluster, then family, then order, then anything — exists
 * because the cluster runs out. A species with no close relatives in the region
 * (Killdeer, Belted Kingfisher, Vaux's Swift) generates no pairings at all, and
 * the old code filled those slots by shuffling the whole pool: a kingfisher
 * question against a chickadee, a wren and a goose. Those are free marks. The
 * learner answers them correctly without knowing the bird, so accuracy inflates
 * and — worse — FSRS reads the easy win as evidence the card is known and
 * pushes the interval out. The distinctive birds end up the least reviewed,
 * which is exactly backwards. Falling through family → order keeps a wrong
 * answer at least plausible: a kingfisher against other Coraciiformes, or
 * failing that other non-passerines.
 *
 * The two broad tiers draw from the target's taxonomic neighbourhood rather
 * than at random — see the comment on them below.
 *
 * Every tier is filtered against `pool`, so a caller narrowing the pool (Photo
 * ID only offers species that have photos) still gets graded fallback within it.
 */
export function pickDistractors(
  targetCode: string,
  pool: string[],
  count: number,
  skill: Skill,
  rnd: () => number = Math.random,
): string[] {
  const target = getSpecies(targetCode);
  const inPool = new Set(pool);
  const chosen: string[] = [];
  const taken = new Set<string>([targetCode]);

  const take = (codes: string[]): void => {
    for (const c of shuffle(codes, rnd)) {
      if (chosen.length >= count) return;
      if (taken.has(c)) continue;
      taken.add(c);
      chosen.push(c);
    }
  };
  const remaining = (): string[] => pool.filter((c) => !taken.has(c));

  take(confusablesFor(target, skill).filter((c) => inPool.has(c)));

  // The taxonomic tiers are skipped rather than matched loosely when the target
  // has no family/order — `undefined === undefined` would otherwise group every
  // species missing the field into one bogus tier.
  if (chosen.length < count && target?.family) {
    take(remaining().filter((c) => getSpecies(c)?.family === target.family));
  }
  // Order and the open pool are both too coarse to draw from at random — order
  // because ~60% of the app is Passeriformes, so "same order" barely narrows
  // anything and an empty-cluster Brown Creeper draws a crow; the open pool
  // because it's the whole app. Taking the taxonomic neighbourhood of each
  // instead gives the creeper the nuthatches and wrens it sits between, and
  // gives the six birds that are the only member of their entire order —
  // Turkey Vulture, Belted Kingfisher, American Kestrel, Common Loon, Great
  // Blue Heron, California Quail — the closest thing the region has to a
  // relative, rather than a songbird picked out of a hat.
  if (chosen.length < count && target?.order) {
    const sameOrder = remaining().filter((c) => getSpecies(c)?.order === target.order);
    take(nearestTo(targetCode, sameOrder, NEIGHBOURHOOD));
  }
  if (chosen.length < count) {
    const sameSide = remaining().filter((c) => isSongbird(c) === isSongbird(targetCode));
    take(nearestTo(targetCode, sameSide, NEIGHBOURHOOD));
  }
  if (chosen.length < count) take(nearestTo(targetCode, remaining(), NEIGHBOURHOOD));
  // Only reachable when the neighbourhood is smaller than the slots left, i.e.
  // a pool of a handful of species.
  if (chosen.length < count) take(remaining());

  return chosen;
}

/**
 * How often a question is built around a confounder instead of the target.
 *
 * Rare on purpose. The graded cascade is the right default — a wrong answer
 * should be a bird you could actually have confused this one with — and a round
 * where every question inverted the shape would just teach the inverse rule.
 * One question in twenty is enough that the shape can't be assumed.
 */
const CONFOUNDER_CHANCE = 0.05;

/**
 * A species with nothing in common with `targetCode`: different family,
 * different order, not in any of its confusable clusters, and on the other side
 * of the songbird line when the pool has one to offer.
 *
 * Returns undefined when the pool is too narrow to be sure of that — a
 * half-related confounder is worse than none, because it reads as a cascade that
 * went wrong rather than a deliberate change of shape.
 */
function pickConfounder(
  targetCode: string,
  pool: string[],
  rnd: () => number,
): string | undefined {
  const target = getSpecies(targetCode);
  if (!target?.order) return undefined;
  const related = new Set<string>([targetCode]);
  for (const skill of ['sound', 'names', 'photo'] as const) {
    for (const c of confusablesFor(target, skill)) related.add(c);
  }
  const far = pool.filter((c) => {
    const sp = getSpecies(c);
    return (
      sp &&
      !related.has(c) &&
      sp.order !== target.order &&
      (!target.family || sp.family !== target.family)
    );
  });
  // Across the songbird line if we can, since that is the division a birder
  // actually sees; anything merely out-of-order will do if we can't.
  const other = far.filter((c) => isSongbird(c) !== isSongbird(targetCode));
  const from = other.length ? other : far;
  if (!from.length) return undefined;
  return from[Math.floor(rnd() * from.length)];
}

/**
 * Target + distractors shuffled into answer order.
 *
 * Normally the four cards are the target and its nearest confusables, so all
 * four are related and the question is genuinely about telling them apart. That
 * has a tell, though: when the cluster runs thin the odd card out is visibly
 * unrelated to the other three and can be discarded without knowing the bird.
 *
 * So `CONFOUNDER_CHANCE` of the time the question is built the other way round:
 * pick a species with nothing to do with the target, fill three cards with it
 * and its own nearest kin, and let the target be the fourth. The three wrong
 * answers still hang together, so "three of these are related" stops being a
 * usable signal — the learner has to actually recognise the bird. If the pool
 * can't produce a clean confounder and enough kin for it, the normal cascade
 * runs instead.
 */
export function buildChoices(
  targetCode: string,
  pool: string[],
  distractorCount: number,
  skill: Skill,
  rnd: () => number = Math.random,
): string[] {
  if (rnd() < CONFOUNDER_CHANCE && distractorCount > 1) {
    const decoys = confounderDistractors(targetCode, pool, distractorCount, skill, rnd);
    if (decoys) return shuffle([targetCode, ...decoys], rnd);
  }
  const distractors = pickDistractors(targetCode, pool, distractorCount, skill, rnd);
  return shuffle([targetCode, ...distractors], rnd);
}

/**
 * Choices for a pair drill: the two birds that get mixed up, plus fill.
 *
 * The ordinary builder picks the wrong answers from the target's cluster, which
 * usually — but only usually — includes the bird the learner keeps naming
 * instead. A drill cannot leave that to chance: the whole question is "this one
 * or that one", so `partner` is placed first and the remaining slots are filled
 * by the normal cascade around the target. No confounder inversion here either;
 * a question where neither of the two is the answer would be a different
 * exercise wearing the drill's name.
 *
 * Falls back to `buildChoices` when the partner can't be used — not in the
 * pool, or the target itself — so a caller never has to check first.
 */
export function buildPairChoices(
  targetCode: string,
  partnerCode: string,
  pool: string[],
  distractorCount: number,
  skill: Skill,
  rnd: () => number = Math.random,
): string[] {
  if (partnerCode === targetCode || !pool.includes(partnerCode)) {
    return buildChoices(targetCode, pool, distractorCount, skill, rnd);
  }
  // The partner is held out of the cascade so it cannot be drawn twice, and the
  // cascade is run against the target as usual: the other two cards are still
  // the birds this one is genuinely confusable with.
  const rest = pickDistractors(
    targetCode,
    pool.filter((c) => c !== partnerCode),
    Math.max(0, distractorCount - 1),
    skill,
    rnd,
  );
  return shuffle([targetCode, partnerCode, ...rest], rnd);
}

/**
 * The confounder and its own nearest kin, `count` of them, or null if the pool
 * cannot fill every slot from around the confounder — a short set would leave
 * the last card drawn from nowhere in particular, which is the free mark this
 * is meant to remove.
 */
function confounderDistractors(
  targetCode: string,
  pool: string[],
  count: number,
  skill: Skill,
  rnd: () => number,
): string[] | null {
  const confounder = pickConfounder(targetCode, pool, rnd);
  if (!confounder) return null;
  // The target is held out of the confounder's own cascade: it is the answer,
  // and it must not turn up a second time as one of its decoys.
  const kin = pickDistractors(
    confounder,
    pool.filter((c) => c !== targetCode),
    count - 1,
    skill,
    rnd,
  );
  if (kin.length < count - 1) return null;
  return [confounder, ...kin];
}
